import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, type ActionMenuItem, type SyutGridColumn } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import FolioModal from 'components/modals/modules/pms-module/FolioModal';
import PostChargeModal from 'components/modals/modules/pms-module/PostChargeModal';
import PaymentCaptureModal from 'components/modals/modules/payment-module/PaymentCaptureModal';
import StayMealsModal from 'components/modals/modules/pms-module/StayMealsModal';
import { propertyService } from 'api/services/propertyService';
import { folioService } from 'api/services/folioService';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { formatCurrency } from 'utils/currencyFormat';
import { settlementModeLabel } from 'api/types/settlement.types';
import { INHOUSE_ACTIONS, type FolioDetail, type InHouseFolioRow } from 'api/types/folio.types';

/**
 * ============================================================================
 * IN-HOUSE GUESTS — the desk's working list (PMS Phase 2 slice 5, P2-14)
 * ============================================================================
 * One row per open folio — a checked-in stay's account — with its rooms, plan,
 * pax, departure, who settles it and where the account stands. The folio opens
 * from here; charges, receipts and room charging are worked from the folio.
 * "Needs attention" narrows to folios with room charging off or past their
 * credit limit. Arrivals and departures stay on the Front Office day sheet.
 * ============================================================================
 */

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

/** SyutGrid keys rows by id: the folio is the row. */
type Row = InHouseFolioRow & { id: string };

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

const InHouseGuestsScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const can = (a: string) => allowedActions.includes(a);

  const { defaultPropertyId } = useDefaultProperty();
  const [propertyId, setPropertyId] = useState('');
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);

  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) { setRows([]); setTotalCount(0); return; }
    setLoading(true);
    try {
      const res = await folioService.inHouse({ propertyId, search: debounced || undefined, attentionOnly, page, pageSize });
      setRows(res.items.map((r) => ({ ...r, id: r.folioId })));
      setTotalCount(res.totalCount);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to load in-house guests.');
    } finally {
      setLoading(false);
    }
  }, [propertyId, debounced, attentionOnly, page, pageSize]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [propertyId, debounced, attentionOnly]);

  // ---- folio, quick post, quick receipt -----------------------------------------------------
  const [folioId, setFolioId] = useState<string | null>(null);
  const [quickFolio, setQuickFolio] = useState<FolioDetail | null>(null);
  const [receiptFor, setReceiptFor] = useState<InHouseFolioRow | null>(null);
  const [mealsFor, setMealsFor] = useState<InHouseFolioRow | null>(null);

  const openQuickPost = useCallback(async (r: InHouseFolioRow) => {
    try {
      setQuickFolio(await folioService.get(r.folioId));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Could not open the folio.');
    }
  }, []);

  // ---- grid ---------------------------------------------------------------------------------
  const columns = useMemo<SyutGridColumn<Row>[]>(() => [
    {
      id: 'roomNumbers', header: 'Room', accessorKey: 'roomNumbers', size: 90,
      cell: ({ row }) => <b>{row.original.roomNumbers || '—'}</b>,
    },
    {
      id: 'guestFullName', header: 'Guest', accessorKey: 'guestFullName', size: 220,
      cell: ({ row }) => (
        <span>
          {row.original.guestFullName || '—'}
          {row.original.isVip && <span className="badge badge-warning" style={{ fontSize: '0.62rem', marginLeft: '0.35rem' }}>VIP</span>}
          {row.original.partyName && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{row.original.partyName}</div>}
        </span>
      ),
    },
    { id: 'mealPlanSummary', header: 'Plan', accessorKey: 'mealPlanSummary', size: 70, cell: ({ row }) => row.original.mealPlanSummary || '—' },
    {
      id: 'pax', header: 'Pax', accessorKey: 'adults', size: 70,
      cell: ({ row }) => `${row.original.adults}${row.original.children ? ` + ${row.original.children}` : ''}`,
    },
    {
      id: 'stay', header: 'Stay', accessorKey: 'expectedCheckOut', size: 140,
      cell: ({ row }) => `${fmtDate(row.original.checkedInAt)} → ${fmtDate(row.original.expectedCheckOut)}`,
    },
    {
      id: 'settlementMode', header: 'Settlement', accessorKey: 'settlementMode', size: 150,
      cell: ({ row }) => settlementModeLabel(row.original.settlementMode) || 'Guest pays',
    },
    { id: 'nationality', header: 'Nationality', accessorKey: 'nationality', size: 110, cell: ({ row }) => row.original.nationality || '—' },
    {
      id: 'folioNumber', header: 'Folio', accessorKey: 'folioNumber', size: 140,
      cell: ({ row }) => (
        <button type="button" className="link-button" title="Open the folio" onClick={() => setFolioId(row.original.folioId)}
          style={{ fontFamily: 'monospace', background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer' }}>
          {row.original.folioNumber}
        </button>
      ),
    },
    {
      id: 'currentBalance', header: 'Balance', accessorKey: 'currentBalance', size: 140,
      cell: ({ row }) => {
        const r = row.original;
        const over = r.creditLimit != null && r.currentBalance > r.creditLimit;
        return (
          <span style={{ color: over ? 'var(--danger, #dc2626)' : r.currentBalance < 0 ? 'var(--success, #16a34a)' : undefined }}
            title={r.currentBalance < 0 ? 'Credit held (advance)' : over ? `Over the credit limit of ${formatCurrency(r.creditLimit)}` : undefined}>
            {formatCurrency(r.currentBalance)}
            {r.partyCharges !== 0 && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{formatCurrency(r.partyCharges)} to party</div>}
          </span>
        );
      },
    },
    {
      id: 'flags', header: '', accessorKey: 'noPost', size: 120, enableSorting: false,
      cell: ({ row }) => (row.original.noPost
        ? <span className="badge badge-warning" style={{ fontSize: '0.65rem' }} title="Outlets must collect directly">Room charging off</span>
        : null),
    },
  ], []);

  const getRowActions = useCallback((r: Row): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'folio', label: 'Folio', icon: <IconSystem.list size={18} />, onClick: () => setFolioId(r.folioId) },
      // Read with In-House view; changing the MAP meal needs `meals` (the dialog and the server check it).
      { id: 'meals', label: 'Meals', icon: <IconSystem.calendar size={18} />, onClick: () => setMealsFor(r) },
    ];
    if (can(INHOUSE_ACTIONS.postCharge))
      items.push({ id: 'post', label: 'Post charge', icon: <IconSystem.add size={18} />, onClick: () => { void openQuickPost(r); } });
    if (can(INHOUSE_ACTIONS.receivePayment))
      items.push({ id: 'receive', label: 'Receive payment', icon: <IconSystem.creditCard size={18} />, onClick: () => setReceiptFor(r) });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedActions, openQuickPost]);

  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(() => { void load(); })];

  return (
    <EnterpriseScreenShell
      title="In-House Guests"
      description="Every checked-in stay and its account — open the folio to post charges, take payments and control room charging."
      icon={<IconSystem.users size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
    >
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div style={{ minWidth: 260 }}>
          <SyutSelect
            label="Property"
            isAsync
            loadOptions={loadPropertyOptions}
            defaultOptions
            cacheOptions={false}
            value={propertyId}
            onChange={(v) => setPropertyId((v as string) || '')}
            placeholder="Select property..."
            size="sm"
            menuPortalTarget={document.body}
          />
        </div>
        <div style={{ minWidth: 260 }}>
          <label className="form-label">Search</label>
          <input type="text" className="form-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Guest, room, booking, GRC, folio or party" />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', paddingBottom: '0.4rem' }}>
          <input type="checkbox" checked={attentionOnly} onChange={(e) => setAttentionOnly(e.target.checked)} />
          Needs attention (room charging off / over limit)
        </label>
        <span className="badge badge-info" style={{ marginBottom: '0.45rem' }}>{totalCount} stay(s)</span>
      </div>

      <div className="data-grid-container">
        <SyutGrid
          data={rows}
          columns={columns}
          enablePagination
          manualPagination
          totalCount={totalCount}
          currentPage={page - 1}
          pageSize={pageSize}
          onPageChange={(i) => setPage(i + 1)}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          isLoading={loading}
          rowActions={getRowActions}
          frozenRightColumns={['actions']}
          emptyMessage={propertyId ? 'Nobody is checked in' : 'Select a property'}
        />
      </div>

      <FolioModal
        isOpen={!!folioId}
        folioId={folioId}
        allowedActions={allowedActions}
        onClose={() => setFolioId(null)}
        onChanged={() => { void load(); }}
      />

      <PostChargeModal
        isOpen={!!quickFolio}
        folio={quickFolio}
        allowedActions={allowedActions}
        onClose={() => setQuickFolio(null)}
        onPosted={() => { setQuickFolio(null); void load(); }}
      />

      <StayMealsModal
        isOpen={!!mealsFor}
        bookingId={mealsFor?.bookingId ?? null}
        roomNumbers={mealsFor?.roomNumbers ?? undefined}
        onClose={() => setMealsFor(null)}
      />

      {receiptFor && (
        <PaymentCaptureModal
          isOpen
          mode="create"
          collectionPoint="FRONT_OFFICE"
          bookingId={receiptFor.bookingId}
          propertyId={propertyId}
          bookingNumber={receiptFor.bookingNumber}
          suggestedAmount={Math.max(0, receiptFor.currentBalance - receiptFor.partyCharges)}
          onSave={() => { setReceiptFor(null); void load(); }}
          onClose={() => setReceiptFor(null)}
        />
      )}
    </EnterpriseScreenShell>
  );
};

export default InHouseGuestsScreen;
